import Image, { type ImageProps } from 'next/image';
import React, { forwardRef, useReducer, type ReactElement, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { initialState, progressiveImageReducer } from './progressiveImageReducer';

interface RequiredImageProps extends ImageProps {
    src: string;
    shouldLazyLoad?: boolean;
    fallbackComponent?: ReactNode;
    loadingComponent?: ReactNode;
    containerClassName?: string;
}

interface ImagePropsWithSize extends RequiredImageProps {
    height: Required<ImageProps>['height'];
    width: Required<ImageProps>['width'];
}

interface ImagePropsWithFill extends RequiredImageProps {
    fill: Required<ImageProps['fill']>;
}

type ProgressiveImageProps = ImagePropsWithSize | ImagePropsWithFill;

const ProgressiveImage = forwardRef<HTMLImageElement, ProgressiveImageProps>(
    (
        {
            id,
            className,
            src,
            alt = '',
            height,
            width,
            shouldLazyLoad = false,
            fallbackComponent,
            loadingComponent,
            containerClassName,
            onError,
            onLoad,
            fill,
            ...other
        },
        ref,
    ): ReactElement => {
        const [state, dispatch] = useReducer(progressiveImageReducer, initialState);
        const { imageError, imageLoading } = state;
        const loadingType = shouldLazyLoad ? 'lazy' : 'eager';

        const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
            dispatch({ type: 'SET_ERROR' });
            onError?.(event);
        };

        const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
            dispatch({ type: 'SET_LOADED' });
            onLoad?.(event);
        };

        if (imageError) {
            return (
                <div
                    className={twMerge('flex bg-[#012F5F]', containerClassName)}
                    style={{
                        height,
                        width,
                    }}
                >
                    {fallbackComponent}
                </div>
            );
        }

        return (
            <div className={containerClassName}>
                {imageLoading && loadingComponent}
                <Image
                    ref={ref}
                    className={className}
                    id={id}
                    src={src}
                    alt={alt}
                    width={width}
                    height={height}
                    fill={fill}
                    loading={loadingType}
                    onError={handleImageError}
                    onLoad={handleImageLoad}
                    {...other}
                    placeholder="empty"
                />
            </div>
        );
    },
);

ProgressiveImage.displayName = 'ProgressiveImage';

export default ProgressiveImage;
